import {AfterViewInit, Component, Input, OnDestroy, ViewChild} from '@angular/core';
import {ReplaySubject} from 'rxjs';
import {NgForm} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {UserPermissions} from '../../../shared/interface/user-permissions';
import {IdentifierService} from '../../../shared/services/identifier.service';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AuthService} from '../../../shared/services/auth.service';
import {delay, distinctUntilChanged, startWith, takeUntil, tap} from 'rxjs/operators';
import {CountedAndPaginated} from '../../../shared/interface/response/counted-and-paginated';
import {Uuid} from '../../../shared/interface/response/uuid';
import {JobResultDialogComponent} from '../../../shared/components/job-result-dialog/job-result-dialog.component';
import {HttpErrorResponse} from '@angular/common/http';
import {RegistrationIdImport} from '../../../shared/interface/registration-id-import';
import {ImportResultDialogComponent} from '../import-result-dialog/import-result-dialog.component';
import {IMPORT_STATUS_IN_PROGRESS} from '../../../shared/services/dict.service';
import {FilterService} from '../../../shared/services/filter.service';
import {ActivatedRoute, Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {StoreRootState} from '../../../shared/ngrx/reducers';
import {getCurrentRouteState} from '../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../shared/ngrx/route-serializer';
import {SelectionModel} from '@angular/cdk/collections';
import {InsuranceCompany} from "../../../shared/interface/insurance-company";
@Component({
  selector: 'app-all-identifier-imports-list',
  templateUrl: './all-imports-list.component.html',
  styleUrls: ['./all-imports-list.component.scss']
})
export class AllImportsListComponent implements AfterViewInit, OnDestroy {
  @Input() disableBreadcrumb = false;
  @Input() disableFilters = false;
  @Input() ofwcaId?: string = null;
  @Input() excludeColumns: string[] = null;
  @Input() importType?: number[] = null;
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @Input() insuranceCompanyId?: number = null;
  protocolOpened = false;
  readonly IMPORT_STATUS_IN_PROGRESS = IMPORT_STATUS_IN_PROGRESS;
  selection = new SelectionModel<RegistrationIdImport>(false, []);
  initialFilters: any;
  imports: RegistrationIdImport[] = [];
  displayedColumns: string[] = [
    'select', 'importType', 'company', 'insuranceCompany', 'changeToRegistered',
    'changeToDeregistered', 'createdAt', 'sentAt', 'createdBy', 'fullReport', 'status',
    'linkName', 'ofwca', 'partner', 'affiliateLink', 'actions',
  ];
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;
  typeOnlyKeys: any = undefined;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private identifierService: IdentifierService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private auth: AuthService,
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private store: Store<StoreRootState>,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.identifierService.importListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<RegistrationIdImport>) => {
          this.imports = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      importType: 'int',
      insuranceCompany: 'int[]',
      company: 'int',
      changeToRegistered: 'int',
      changeToDeregistered: 'int',
      gte$createdAt: 'date',
      lte$createdAt: 'date',
      createdBy: 'string',
      status: 'int',
      // fullReport: 'int',
      // sentAt: 'date',
    }, filters);
  }

  ngAfterViewInit(): void {
    this.activatedRoute.data
      .pipe(
        delay(0),
        takeUntil(this.destroyed$)
      )
      .subscribe((data: any) => {
        this.ofwcaId = this.ofwcaId ?? data.ofwcaId ?? null;
        this.excludeColumns = this.excludeColumns ?? data.excludeColumns ?? [];
        this.displayedColumns = this.displayedColumns.filter(
          (col) => this.excludeColumns.indexOf(col) === -1
        );

        this.paginator.page
          .pipe(takeUntil(this.destroyed$))
          .subscribe((event: PageEvent) => {
            this.router.navigate([
              this.activatedRoute.snapshot.routeConfig.path,
              this.filterService.parseFiltersToQueryParams({
                page: event.pageIndex,
                pageSize: event.pageSize,
                ...this.filtersForm.value,
              }),
            ]);
          });

        this.store
          .pipe(
            select(getCurrentRouteState),
            delay(0),
            distinctUntilChanged(
              (prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url
            ),
            takeUntil(this.destroyed$)
          )
          .subscribe((state: RouterStateUrl) => {
            this.parseFilters(state.params);
            this.paginator.pageIndex = state.params.page;
            this.paginator.pageSize = state.params.pageSize;
            this.loadListData();
          });
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadListData(): void {
    const filters = { ...this.initialFilters };

    if (this.importType) {
      filters.importType = this.importType;
    }

    this.identifierService.loadImportList(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      filters
    );
  }

  filter(): void {
    if (this.paginator.pageIndex === 0) {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: this.paginator.pageIndex,
        pageSize: this.paginator.pageSize,
        ...this.filtersForm.value
      })]);
    } else {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: 0,
        pageSize: this.paginator.pageSize,
        ...this.filtersForm.value,
      })]);
    }
  }

  downloadReport(): void {
    this.identifierService.downloadImportsReport(this.filtersForm.value)
      .subscribe((response: Uuid) => {
        const dialogRef = this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  showResult(registrationIdImport: RegistrationIdImport): void {
    this.dialog.open(ImportResultDialogComponent, {
      width: '500px',
      maxHeight: '80vh',
      data: {registrationIdImportObject: registrationIdImport},
    });
  }

  onImgError($event: ErrorEvent): void {
    // @ts-ignore
    $event.target.src = '/assets/avatar.png';
  }

  downloadImportFile(registrationIdImport: RegistrationIdImport): void {
    this.identifierService.downloadImportFile(registrationIdImport.uuid);
  }

  downloadBackup(registrationIdImport: RegistrationIdImport): void {
    this.identifierService.downloadImportBackup(registrationIdImport.uuid);
  }
  sendImportsReport(): void {
    if (this.selection.selected.length > 0) {
      this.snack.open('Wysyłanie identyfikatorów z ' + this.selection.selected.length.toString() + ' TU','',{duration: 2000} );

      this.selection.selected.forEach((registrationIdImport: RegistrationIdImport) => {
        this.identifierService.downloadImportsValueReport(registrationIdImport.uuid)
          .subscribe((response: Uuid) => {
            const dialogRef = this.dialog.open(JobResultDialogComponent, {
              disableClose: true,
              width: '500px',
              data: {jobId: response.uuid, showProgress: false},
            });

            dialogRef.afterClosed().subscribe(result => {
            });
          }, (error: HttpErrorResponse) => {
            this.snack.open('Przy wysyłąniu identyfikatorów wystąpił błąd', error.status.toString(), {duration: 2000});
            if (error.status === 400) {
              this.snack.open(error.error.error.text, '400', {duration: 4000});
            }
          });
      });
    } else {
      this.snack.open('Nie wybrano żadnego pliku do importu','',{duration: 2000} );
    }
    this.protocolOpened = false;
    this.selection.clear();
  }
  openProtocol(): void {
    this.protocolOpened = true;
  }
  cancel(): void {
    this.protocolOpened = false;
    this.selection.clear();
  }

  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.imports.length;
    return numSelected === numRows;
  }
  isSelect(row?: string): boolean {
    let max = false;
    this.selection.selected.forEach((registrationIdImport: RegistrationIdImport) => {
      if (row === registrationIdImport.uuid){
        max = true;
      }
    });
    return max;
  }
  toggleAllRows(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }
    this.selection.select(...this.imports);
  }
  checkboxLabel(row?: RegistrationIdImport): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.uuid}`;
  }

  insuranceCompanyFilterChanged(insuranceCompanies: InsuranceCompany[]): void {
    const registrationIdTypes = [];
    insuranceCompanies.forEach((insuranceCompany: InsuranceCompany) => {
      registrationIdTypes.push(...insuranceCompany.registrationIdTypes);
    });

    if (registrationIdTypes.length > 0) {
      this.typeOnlyKeys = registrationIdTypes;
    } else {
      this.typeOnlyKeys = undefined;
    }
  }
}
